import {addDays, startOfDay} from 'date-fns';
import {Button, DataStatus, Pagination, Search} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useSearchTestDriveCalendarQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, RequiredTestIdProps, suffixTestId, useDebouncedValue} from 'shared/web';

interface TestDriveCalendarVehicleDialogProps extends RequiredTestIdProps {
  onVehicleSelect: (vehicle: {vehicleId: string; branchId: string}) => void;
}

const SEARCH_DEBOUNCE_MS = 500;
const PAGE_SIZE = 50;
const LIST_HEIGHT = '60vh';

export function TestDriveCalendarVehicleDialog(props: TestDriveCalendarVehicleDialogProps) {
  const [search, setSearch] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const from = startOfDay(new Date());

  const {data, isError, isFetching} = useSearchTestDriveCalendarQuery({
    from: from.toISOString(),
    to: addDays(from, 1).toISOString(),
    search: debouncedSearch,
    pagination: {page, size: PAGE_SIZE},
    includeVehiclesWithoutTestDrives: true,
  });

  const vehicles = data?.vehicles ?? [];
  const totalPages = Math.ceil((data?.totalCount ?? 0) / PAGE_SIZE);

  const handleSearchChange = (value: string | null) => {
    setSearch(value);
    setPage(1);
  };

  return (
    <VStack spacing={4}>
      <Search
        value={search}
        onChange={handleSearchChange}
        data-testid={suffixTestId('search', props)}
      />
      <Box height={LIST_HEIGHT} overflow="auto">
        <DataStatus
          isLoading={isFetching}
          isError={isError}
          isEmpty={vehicles.length === 0}
          emptyMessage={i18n.t('entity.vehicle.labels.noMatchingVehicles')}
          minHeight={40}
          data-testid={suffixTestId('dataStatus', props)}
        >
          {vehicles.map((vehicle) => (
            <Box
              key={vehicle.vehicleId}
              borderColor="palettes.neutral.40.100"
              borderBottom="1px solid"
              paddingVertical={3}
            >
              <HStack align="center" justify="space-between" spacing={3}>
                <VStack spacing={1}>
                  <Text
                    size="small"
                    data-testid={suffixTestId(`title-${vehicle.vehicleId}`, props)}
                  >
                    {vehicle.title}
                  </Text>
                  <Text size="xSmall" color="secondary">
                    {`${isNotNilOrEmpty(vehicle.registrationPlate) ? vehicle.registrationPlate : EMPTY_PLACEHOLDER} · ${vehicle.branchName}`}
                  </Text>
                </VStack>
                <Button
                  title={i18n.t('general.actions.select')}
                  variant="link"
                  size="small"
                  onClick={() =>
                    props.onVehicleSelect({
                      vehicleId: vehicle.vehicleId,
                      branchId: vehicle.branchId,
                    })
                  }
                  data-testid={suffixTestId(`select-${vehicle.vehicleId}`, props)}
                />
              </HStack>
            </Box>
          ))}
        </DataStatus>
      </Box>
      <Show when={totalPages > 1}>
        <Pagination
          page={page}
          pagesQuantity={totalPages}
          onPageChange={setPage}
          data-testid={suffixTestId('pagination', props)}
        />
      </Show>
    </VStack>
  );
}
